import { App, Plan } from '@back4app2/sdk';

/**
 * The paid plan a free choice turns into when a paid-only option is picked —
 * and the one an Upgrade from Free buys straight away.
 *
 * Its own module: the creation flow and the app shell both need it, and
 * neither should import the other for one sort.
 */
export const cheapestPaidPlan = (plans: Plan[]): Plan | undefined =>
  plans
    .filter((plan) => plan.price > 0)
    .sort((a, b) => a.price - b.price)[0];

/**
 * Whether the app runs on the Free plan — the same '%Free%' test the domain
 * sweeper and `triggerManualDeployment` run on the server.
 */
export const isFreePlanApp = (app: App): boolean =>
  !!app.mainService?.mainServiceEnvironment?.plan?.name?.includes('Free');

/** RAM as the Plan table writes it: MB below 1 GB, GB above. */
export const formatRAM = (ram?: number) =>
  typeof ram === 'number' ? (ram >= 1024 ? `${ram / 1024} GB` : `${ram} MB`) : '';

/** Monthly transfer as the Plan table writes it. */
export const formatTransfer = (gb?: number) =>
  typeof gb === 'number' ? (gb >= 1000 ? `${gb / 1000} TB` : `${gb} GB`) : '';

/**
 * What the buyer gets, for the checkout summary: one line per thing the plan
 * row says, and only what this plan object actually carries. Screens load
 * plans through different queries, so a field missing here drops its line
 * rather than guessing.
 *
 * Permanent URL and Always on are what paid buys over Free: only Free has an
 * expiring URL and sleeps. Price, not `mustSleep`, because not every plan
 * query carries `mustSleep` and the checkout only ever sells paid plans.
 */
export const planFeatures = (
  plan: Partial<Pick<Plan, 'price' | 'maxCPU' | 'maxRAM' | 'transferLimit'>>
): string[] => {
  const isPaid = plan.price !== 0;

  return [
    isPaid ? 'Permanent URL — your link stops expiring' : '',
    isPaid ? 'Always on — no sleeping when idle' : '',
    typeof plan.maxCPU === 'number' && plan.maxRAM
      ? `${plan.maxCPU} vCPU · ${formatRAM(plan.maxRAM)} RAM`
      : '',
    typeof plan.transferLimit === 'number' ? `${formatTransfer(plan.transferLimit)} transfer / month` : '',
  ].filter(Boolean);
};
